import type { AxiosInstance } from 'axios';

export interface Category {
  id: string;
  parentId: string | null;
  name: string;
  slug: string;
  description: string | null;
  imageUrl?: string | null;
  iconName?: string | null;
  /** Icon colour, #RRGGBB; null lets the apps pick one. */
  iconColor?: string | null;
  commissionBps: number;
  /** Days to return; 0 = not returnable; null = inherit (parent, then 7). */
  returnWindowDays?: number | null;
  position: number;
  active: boolean;
  createdAt: string;
  _count?: { children: number; products: number };
}

export interface CategoryNode extends Category {
  children: CategoryNode[];
}

const unwrap = <T>(p: Promise<{ data: { data: T } }>) => p.then((r) => r.data.data);

export function categoryEndpoints(http: AxiosInstance) {
  return {
    list: (params?: { parentId?: string; active?: boolean }) =>
      unwrap<Category[]>(http.get('/categories', { params })),
    tree: () => unwrap<CategoryNode[]>(http.get('/categories/tree')),
    getById: (categoryId: string) =>
      http.get<Category>(`/categories/${categoryId}`).then((r) => r.data),
    create: (body: {
      name: string;
      slug: string;
      parentId?: string;
      description?: string;
      imageUrl?: string;
      iconName?: string;
      iconColor?: string;
      commissionBps?: number;
      returnWindowDays?: number | null;
      position?: number;
    }) => http.post<Category>('/categories', body).then((r) => r.data),
    update: (
      categoryId: string,
      body: Partial<{
        name: string;
        slug: string;
        parentId: string | null;
        description: string;
        imageUrl: string | null;
        iconName: string | null;
        iconColor: string | null;
        commissionBps: number;
        returnWindowDays: number | null;
        position: number;
        active: boolean;
      }>,
    ) => http.patch<Category>(`/categories/${categoryId}`, body).then((r) => r.data),
    remove: (categoryId: string) => http.delete(`/categories/${categoryId}`).then((r) => r.data),
  };
}

export type CategoryEndpoints = ReturnType<typeof categoryEndpoints>;
